import { FilePdf, Sparkle, UploadSimple } from 'phosphor-react';
import { useEffect, useRef, useState } from 'react';
import {
  AiAutomationSettings,
  EmailAgentDocumentType,
  EmailAgentSendBehavior,
  Supplier,
} from '@dill/dill-shared';
import { DillButton, DillCheckbox, DillSelect, DillToggle } from '../../../../../general/Dill';
import { COLORS } from '../../../../../../utils/colors';
import { firebaseFileUploadHelper, openDillToast } from '../../../../../../utils/helpers';
import { useAppDispatch } from '../../../../../../redux/store';
import { getUserDetails } from '../../../../../../redux/services/authService';
import { updateAiAutomationSettingsThunk } from '../../../../../../redux/services/emailAgentService';
import { APP_ENVIRONMENT } from '../../../../../../utils/constants';

type DocumentType = EmailAgentDocumentType;
type SendBehavior = EmailAgentSendBehavior;

const DOCUMENT_TYPE_OPTIONS: { label: string; value: DocumentType }[] = [
  { label: 'Invoices', value: 'INVOICES' },
  { label: 'Statements', value: 'STATEMENTS' },
  { label: 'W-9', value: 'W9' },
];

const SEND_BEHAVIOR_OPTIONS: { label: string; value: SendBehavior }[] = [
  { label: 'Auto-draft — a team member reviews and sends', value: 'AUTO_DRAFT' },
  { label: 'Auto-send — Dill AI agentically replies with no review', value: 'AUTO_SEND' },
];

type DocumentTypeSettings = Record<DocumentType, SendBehavior>;

const DEFAULT_DOCUMENT_TYPE_SETTINGS: DocumentTypeSettings = {
  INVOICES: 'AUTO_DRAFT',
  STATEMENTS: 'AUTO_DRAFT',
  W9: 'AUTO_DRAFT',
};

const DEFAULT_DOCUMENT_TYPES: DocumentType[] = ['INVOICES', 'STATEMENTS'];

const ALL_DOCUMENT_TYPES: DocumentType[] = ['INVOICES', 'STATEMENTS', 'W9'];

export const AiAutomationSettingsSection = ({ supplier }: { supplier?: Supplier | null }) => {
  const dispatch = useAppDispatch();
  const [autoAcceptRecommendations, setAutoAcceptRecommendations] = useState(false);
  const [documentResponseEnabled, setDocumentResponseEnabled] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>(DEFAULT_DOCUMENT_TYPES);
  const [documentTypeSendBehavior, setDocumentTypeSendBehavior] = useState<DocumentTypeSettings>(
    DEFAULT_DOCUMENT_TYPE_SETTINGS
  );
  const [w9File, setW9File] = useState<{ name?: string | null; url: string } | null>(null);
  const [isUploadingW9, setIsUploadingW9] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const w9InputRef = useRef<HTMLInputElement>(null);
  const showAutoAccept = APP_ENVIRONMENT !== 'production';
  const showDocumentRequestResponse = supplier?.isAiDocumentRequestResponseEnabled === true;

  // Load what is saved on the supplier; re-run when the supplier record is refreshed after a save.
  useEffect(() => {
    const saved = supplier?.aiAutomationSettings;
    setAutoAcceptRecommendations(!!saved?.autoAcceptRecommendations);
    const response = saved?.documentRequestResponse;
    setDocumentResponseEnabled(!!response?.enabled);
    if (response?.documentTypes) {
      setDocumentTypes(
        ALL_DOCUMENT_TYPES.filter((type) => response.documentTypes?.[type]?.enabled)
      );
      setDocumentTypeSendBehavior(
        ALL_DOCUMENT_TYPES.reduce(
          (acc, type) => ({
            ...acc,
            [type]: response.documentTypes?.[type]?.sendBehavior ?? 'AUTO_DRAFT',
          }),
          { ...DEFAULT_DOCUMENT_TYPE_SETTINGS }
        )
      );
    } else {
      setDocumentTypes(DEFAULT_DOCUMENT_TYPES);
      setDocumentTypeSendBehavior(DEFAULT_DOCUMENT_TYPE_SETTINGS);
    }
    setW9File(response?.w9File ?? null);
  }, [supplier?.id, supplier?.aiAutomationSettings]);

  const setSendBehaviorFor = (documentType: DocumentType, behavior: SendBehavior) => {
    setDocumentTypeSendBehavior((prev) => ({ ...prev, [documentType]: behavior }));
  };

  const handleW9Selected = async (file?: File | null) => {
    if (!file || !supplier?.id) {
      return;
    }
    if (file.type !== 'application/pdf') {
      openDillToast({ message: 'Upload the W-9 as a PDF.', type: 'ERROR' });
      return;
    }
    setIsUploadingW9(true);
    const blobUrl = URL.createObjectURL(file);
    try {
      const url = await firebaseFileUploadHelper({
        blobUrl,
        storagePath: `suppliers/${supplier.id}/w9`,
        fileNameWithExtension: `w9-${Date.now()}.pdf`,
      });
      if (url) {
        setW9File({ name: file.name, url });
      } else {
        openDillToast({ message: 'Failed to upload the W-9.', type: 'ERROR' });
      }
    } catch (error) {
      openDillToast({ message: 'Failed to upload the W-9.', type: 'ERROR' });
    } finally {
      URL.revokeObjectURL(blobUrl);
      setIsUploadingW9(false);
    }
  };

  const handleSave = async () => {
    if (!supplier?.id) {
      return;
    }
    if (
      showDocumentRequestResponse &&
      documentResponseEnabled &&
      documentTypes.includes('W9') &&
      !w9File
    ) {
      openDillToast({ message: 'Upload your W-9 before enabling W-9 responses.', type: 'ERROR' });
      return;
    }
    const aiAutomationSettings: AiAutomationSettings = {
      autoAcceptRecommendations,
      documentRequestResponse: {
        enabled: documentResponseEnabled,
        documentTypes: ALL_DOCUMENT_TYPES.reduce(
          (acc, type) => ({
            ...acc,
            [type]: {
              enabled: documentTypes.includes(type),
              sendBehavior: documentTypeSendBehavior[type],
            },
          }),
          {}
        ),
        w9File,
      },
    };
    setIsSaving(true);
    const result = await dispatch(
      updateAiAutomationSettingsThunk({ supplierId: supplier.id, aiAutomationSettings })
    );
    setIsSaving(false);
    if (updateAiAutomationSettingsThunk.fulfilled.match(result)) {
      openDillToast({ message: 'AI automation settings saved.', type: 'SUCCESS' });
      dispatch(getUserDetails());
    } else {
      openDillToast({
        message: (result.payload as string) || 'Failed to save AI automation settings.',
        type: 'ERROR',
      });
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-purple-50">
          <Sparkle size={16} color={COLORS.PRIMARY_500} weight="bold" />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">AI Automation</h4>
          <p className="text-xs text-gray-400 mt-0.5">
            Control how much Dill's AI can act on its own for {supplier?.name || 'this supplier'}.
          </p>
        </div>
      </div>

      {/* Auto-accept collection recommendations */}
      {showAutoAccept && (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">Auto-accept AI recommendations</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              When on, Dill AI's collection task recommendations (reminders, escalations, holds) are
              applied automatically — no team member has to review and accept them first.
            </p>
          </div>
          <DillToggle value={autoAcceptRecommendations} onChange={setAutoAcceptRecommendations} />
        </div>
      )}

      {/* AI Document Request Response */}
      {showDocumentRequestResponse && (
        <div
          className={`${
            showAutoAccept ? 'mt-3 ' : ''
          }rounded-lg border border-gray-200 bg-white px-4 py-3`}>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">AI Document Request Response</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                When a customer emails asking for a document, Dill AI finds it and responds for you.
                Requires at least one connected inbox below.
              </p>
            </div>
            <DillToggle value={documentResponseEnabled} onChange={setDocumentResponseEnabled} />
          </div>

          {documentResponseEnabled && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-3">
              <p className="text-xs font-medium text-gray-600">Respond to requests for</p>
              {DOCUMENT_TYPE_OPTIONS.map(({ label, value }) => {
                const isEnabled = documentTypes.includes(value);
                return (
                  <div
                    key={value}
                    className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                    <DillCheckbox
                      options={[{ label, value }]}
                      value={isEnabled ? [value] : []}
                      onChange={(v: string[]) =>
                        setDocumentTypes(
                          v.length > 0
                            ? [...documentTypes.filter((t) => t !== value), value]
                            : documentTypes.filter((t) => t !== value)
                        )
                      }
                    />
                    <DillSelect
                      value={documentTypeSendBehavior[value]}
                      onChange={(v) => setSendBehaviorFor(value, v as SendBehavior)}
                      options={SEND_BEHAVIOR_OPTIONS}
                      disabled={!isEnabled}
                    />
                    {value === 'W9' && isEnabled && (
                      <div className="flex items-center justify-between gap-2">
                        {w9File ? (
                          <a
                            href={w9File.url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1.5 text-xs text-blue-600 truncate">
                            <FilePdf size={14} />
                            {w9File.name || 'W-9.pdf'}
                          </a>
                        ) : (
                          <p className="text-[11px] text-gray-500">
                            Upload the W-9 Dill AI should send.
                          </p>
                        )}
                        <input
                          ref={w9InputRef}
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          onChange={(event) => {
                            handleW9Selected(event.target.files?.[0]);
                            event.target.value = '';
                          }}
                        />
                        <DillButton
                          label={w9File ? 'Replace' : 'Upload W-9'}
                          variant="secondary"
                          icon={<UploadSimple size={13} />}
                          onClick={() => w9InputRef.current?.click()}
                          isLoading={isUploadingW9}
                          isDisabled={isUploadingW9}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Dill AI only answers senders listed as a contact on a customer, and only with that
                customer's own documents. Auto-send falls back to a draft whenever the request is
                unclear, an invoice can't be found, or the sender's email can't be verified.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <DillButton
          label="Save"
          onClick={handleSave}
          isLoading={isSaving}
          isDisabled={isSaving || isUploadingW9}
          height={36}
        />
      </div>
    </div>
  );
};
